"use client";

import { useCallback, useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { Bell, Loader2 } from "lucide-react";
import { useAuth } from "@/providers/auth-provider";
import { UserAvatar } from "@/components/ui/user-avatar";
import { timeAgo } from "@/lib/format-utils";
import { TYPE_ICON } from "@/components/notifications/notification-icons";
import { NotificationMenuContent } from "@/components/notifications/notification-presentations";
import { fetchUnreadCount } from "@/hooks/use-unread-notifications";
import { getNotifications, markNotificationRead, markAllNotificationsRead, type NotificationView } from "@/lib/actions/notification-actions";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

/**
 * Navbar notification bell: unread badge + dropdown feed.
 *
 * Freshness is fetch-on-load (matches the app's existing pattern — no polling
 * or realtime yet): the unread count is fetched once when the signed-in user
 * mounts, and the list is lazily loaded the first time the dropdown opens.
 * Marking read updates local state optimistically and reverts if the write
 * fails, so the badge never lies about server state for long.
 */
export function NotificationBell() {
  const {
    user
  } = useAuth();
  const router = useRouter();
  const [unread, setUnread] = useState<number | null>(null);
  const [items, setItems] = useState<NotificationView[] | null>(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);

  // Fetch the unread count once on mount (per signed-in user).
  useEffect(() => {
    if (!user) return;
    let active = true;
    fetchUnreadCount(user.id).then(count => {
      if (active) setUnread(count);
    }).catch(() => {
      // Leave the badge hidden on failure rather than inventing a count.
      if (active) setUnread(null);
    });
    return () => {
      active = false;
    };
  }, [user]);
  const loadList = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const rows = await getNotifications();
      setItems(rows);
      // Reconcile the badge with the freshly loaded list.
      setUnread(rows.filter(r => !r.read).length);
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);
  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    // Load on first open, and retry if a previous open failed to load.
    if (next && (items === null || loadError)) {
      void loadList();
    }
  };
  const markOneRead = (n: NotificationView) => {
    if (n.read) return;
    // Optimistic: flip locally, then persist; revert both on failure.
    setItems(prev => prev ? prev.map(it => it.id === n.id ? {
      ...it,
      read: true
    } : it) : prev);
    setUnread(c => c === null ? c : Math.max(0, c - 1));
    void markNotificationRead(n.id).catch(() => {
      setItems(prev_0 => prev_0 ? prev_0.map(it_0 => it_0.id === n.id ? {
        ...it_0,
        read: false
      } : it_0) : prev_0);
      setUnread(c_0 => c_0 === null ? c_0 : c_0 + 1);
    });
  };
  const handleItemSelect = (n_0: NotificationView) => {
    markOneRead(n_0);
    if (n_0.linkUrl) router.push(n_0.linkUrl);
  };
  const handleMarkAll = () => {
    const snapshot = items;
    const prevUnread = unread;
    setItems(prev_1 => prev_1 ? prev_1.map(it_1 => ({
      ...it_1,
      read: true
    })) : prev_1);
    setUnread(0);
    void markAllNotificationsRead().catch(() => {
      setItems(snapshot);
      setUnread(prevUnread);
    });
  };
  if (!user) return null;
  const hasUnread = unread !== null && unread > 0;
  const badge = hasUnread ? unread > 99 ? "99+" : unread : null;
  return <DropdownMenu open={open} onOpenChange={handleOpenChange}>
            <DropdownMenuTrigger asChild>
                <button type="button" aria-label={hasUnread ? `Notifications, ${unread} unread` : "Notifications"} className="relative flex size-9 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring cursor-pointer">
                    <Bell className="size-5" />
                    {badge && <span aria-hidden className="absolute -right-0.5 -top-0.5 flex min-w-4 items-center justify-center rounded-full bg-accent px-1 text-[10px] font-bold leading-4 text-white">
                            {badge}
                        </span>}
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-[22rem] max-w-[calc(100vw-1.5rem)] p-0">
                <div className="flex items-center justify-between px-4 py-2.5">
                    <DropdownMenuLabel className="p-0 text-sm font-semibold">
                        Notifications
                    </DropdownMenuLabel>
                    {hasUnread && <DropdownMenuItem onSelect={e => {
          // Keep the menu open after marking all read.
          e.preventDefault();
          handleMarkAll();
        }} className="w-auto px-0 py-0 text-xs font-medium text-primary focus:bg-transparent focus:text-primary">
                            Mark all read
                        </DropdownMenuItem>}
                </div>
                <DropdownMenuSeparator className="my-0" />

                <div className="max-h-[24rem] overflow-y-auto">
                    {loading && <div role="status" className="flex items-center justify-center py-8 text-muted-foreground">
                            <Loader2 className="size-5 animate-spin" aria-hidden />
                            <span className="sr-only">
                                Loading notifications…
                            </span>
                        </div>}

                    {!loading && loadError && <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                            <p>Couldn&apos;t load notifications.</p>
                            <button type="button" onClick={() => void loadList()} className="mt-2 text-sm font-medium text-primary hover:underline cursor-pointer">
                                Try again
                            </button>
                        </div>}

                    {!loading && !loadError && items && items.length === 0 && <p className="px-4 py-8 text-center text-sm text-muted-foreground">
                            You&apos;re all caught up.
                        </p>}

                    {!loading && !loadError && items && items.map(n_1 => {
          const Icon = TYPE_ICON[n_1.type] ?? Bell;
          return <DropdownMenuItem key={n_1.id} onSelect={() => handleItemSelect(n_1)} className={`flex items-start gap-3 rounded-none px-4 py-3 ${n_1.read ? "" : "bg-primary/5"}`}>
                                    {n_1.actorAvatar ? <UserAvatar url={n_1.actorAvatar} name={n_1.actorName ?? ""} size={32} className="mt-0.5 shrink-0" /> : <span className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full bg-secondary text-primary">
                                            <Icon className="size-4" aria-hidden />
                                        </span>}
                                    <NotificationMenuContent title={n_1.title} body={n_1.body} isRead={n_1.read} timeLabel={timeAgo(n_1.createdAt)} />
                                </DropdownMenuItem>;
        })}
                </div>

                <DropdownMenuSeparator className="my-0" />
                <DropdownMenuItem onSelect={() => router.push("/notifications")} className="justify-center rounded-none px-4 py-2.5 text-sm font-medium text-primary focus:text-primary">
                    See all notifications
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>;
}